import { FC, useRef, useMemo, useState, useEffect, useImperativeHandle, forwardRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { RequirementItem } from "@/types/requirement";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import { supplierPricesFetch, supplierPriceAdd } from "@/apis/supplier";
import { SupplierPrice } from "@/types/purchase";
import { App, Button, Flex, Modal, Progress, Spin, InputNumber } from "antd";
import { ItemWithPrice } from "./GeneratePurchase";
import { DictContext } from "@/layout/context/DictContext";
import { getTrueCount } from "@/pages/Requirement/util";

export interface MatchPriceRef {
  getItemPrices: () => Array<ItemWithPrice>;
}

interface MatchPriceProps {
  items: Array<RequirementItem>;
  supplierId: string;
  onMatchRes: (res: "F" | "T") => void;
}

const MatchPrice = forwardRef<MatchPriceRef, MatchPriceProps>(({ items, supplierId, onMatchRes }, ref) => {
  const { t } = useTranslation();
  const { exchangeRate } = useContext(DictContext);
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [loading, setLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [processNum, setProcessNum] = useState<number>(0);
  const [itemPrices, setItemPrices] = useState<Array<ItemWithPrice>>([]);

  useImperativeHandle(ref, () => ({
    getItemPrices: () => itemPrices,
  }), [itemPrices]);

  useEffect(() => {
    fetchPrices();
  }, [supplierId, items]);

  const fetchPrices = async () => {
    if (!supplierId) return;
    const codes = items.map(item => item.product?.code).filter(Boolean) as string[];
    if (codes.length === 0) return;

    setLoading(true);
    try {
      const { body: prices } = await supplierPricesFetch(supplierId, {
        strs: codes,
      });
      const priceMap = new Map(prices.map(p => [p.product.code, p]));
      const newItemPrices: Array<ItemWithPrice> = items.map(item => ({
        ...item,
        price: priceMap.get(item.product?.code ?? ""),
        priceValue: getPrice(priceMap.get(item.product?.code ?? ""), item.purCount)
      }));
      setItemPrices(newItemPrices);

      const hasUnmatched = newItemPrices.some(item => !item.price);
      onMatchRes(hasUnmatched ? "F" : "T");
    } catch (error) {
      console.error('Failed to fetch prices:', error);
      onMatchRes("F");
    } finally {
      setLoading(false);
    }
  };

  // 匹配失败的数据（没有价格的物料）
  const unmatchedItems = useMemo(() => {
    return itemPrices.filter(item => !item.price);
  }, [itemPrices]);

  // 处理价格变更
  const handlePriceChange = (item: ItemWithPrice, value: number | null) => {
    setItemPrices(prev => prev.map(prevItem => {
      if (prevItem.id === item.id) {
        return {
          ...prevItem,
          priceValue: value ?? undefined,
        };
      }
      return prevItem;
    }));
  };

  const getPrice = (price: SupplierPrice | undefined, count: string) => {
    let trueCount = getTrueCount(count, price?.mpq ?? '1')
    let supplierPrice = price?.ladders
      .sort((a, b) => a.count - b.count)
      .find(price => price.count <= Number(trueCount))?.price;
    return Number(supplierPrice ?? '1')
  }

  const handleMatchPrices = async () => {
    if (!supplierId) {
      notification.error({ message: t("requirement.supplierIdError"), placement: "top" });
      return;
    }
    if (unmatchedItems.length === 0) {
      notification.warning({
        message: t("requirement.noNeedAddPrice"),
        description: t("requirement.allProductsHavePrice")
      });
      return;
    }

    setMatchLoading(true);
    const addPrices: SupplierPrice[] = [];
    const failedItems: ItemWithPrice[] = [];

    for (let i = 0; i < unmatchedItems.length; i++) {
      const item = unmatchedItems[i];
      const product = item.product;
      if (!product || !product.id) continue;
      try {
        const addedPrice = await supplierPriceAdd(supplierId, {
          productId: product.id,
          price: "1",
          remark: "",
          effectiveAt: Date.now(),
          expiredAt: 0,
          ladders: [
            { count: 1, discount: "100", price: "1", remark: "" }
          ],
          days: null,
          mpq: null,
          currency: exchangeRate.find(rate => rate.desc === "mainCurrency")?.key
        });
        addPrices.push(addedPrice);
      } catch (error) {
        console.error(`Failed to add price for product ${product.code}:`, error);
        failedItems.push(item);
      }
      setProcessNum(i + 1);
    }

    if (addPrices.length > 0) {
      const priceMap = new Map(addPrices.map(p => [p.product.code, p]));
      setItemPrices(prev => prev.map(item => {
        const newPrice = priceMap.get(item.product?.code ?? "");
        return newPrice ? { ...item, price: newPrice, priceValue: 1 } : item;
      }));
    }

    if (failedItems.length > 0) {
      notification.warning({
        message: `${t("requirement.successAddPrice")} ${addPrices.length} ${t("requirement.priceAddFailed")} ${failedItems.length} 个`,
        description: failedItems.map(item => item.product?.code).join(", "),
        placement: "top"
      });
      onMatchRes("F")
    } else {
      notification.success({
        message: `${t("requirement.successAddPrice")} ${addPrices.length}`,
        placement: "top"
      });
      onMatchRes("T")
    }

    setMatchLoading(false);
  };

  useEffect(() => {
    if (unmatchedItems.length > 0) handleMatchPrices();
  }, [unmatchedItems]);

  // const headerChildren = (
  //   <Flex style={{ width: "100%" }}>
  //     <Button disabled={loading} loading={matchLoading} onClick={handleMatchPrices}>{t("requirement.oneClickAddPrice")}</Button>
  //   </Flex>
  // );

  const headerSub = (
    <Flex style={{ width: "100%" }}>
      {unmatchedItems.length > 0 && <span style={{ color: "red" }}>{t("requirement.productsWithoutPriceTip")}</span>}
    </Flex>
  );

  const columns: ColumnsType<ItemWithPrice> = useMemo(() => [
    {
      title: t("product.code"),
      dataIndex: ['product', 'code'],
      key: 'productCode',
      width: 120,
      render: (_, item) => <SText text={item.product?.code ?? ""} style={{ width: "100%", color: item.price ? undefined : "red" }} />,
    },
    {
      title: t("product.name"),
      dataIndex: ['product', 'name'],
      key: 'productName',
      width: 150,
      render: (_, item) => <SText text={item.product?.name ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.model"),
      dataIndex: ['product', 'model'],
      key: 'productModel',
      width: 120,
      render: (_, item) => <SText text={item.product?.model ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.requireCount"),
      dataIndex: 'useAmount',
      width: 100,
      render: (_, item) => <SText text={item.useAmount ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.requirmentQuantity"),
      dataIndex: 'purCount',
      width: 100,
      render: (_, item) => <SText text={item.purCount ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.uom"),
      dataIndex: ['product', 'uom'],
      key: 'uom',
      width: 100,
      render: (_, item) => <SText text={item.product?.uom ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.price"),
      dataIndex: 'priceValue',
      key: 'priceValue',
      width: 220,
      fixed: "right",
      render: (_value, item) => (
        <InputNumber changeOnWheel
          value={item.priceValue}
          onChange={(val) => handlePriceChange(item, val)}
          min={0}
          precision={2}
          style={{ width: "100%" }}
          placeholder={t("sale.plPrice")}
        />
      ),
    },

  ], [t]);

  return (
    <>
      <STableLayout
        loading={loading}
        dataSource={itemPrices}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={"max-content"}
        headerChildren={null}
        headerSub={headerSub}
        tableLayoutRef={tableRef}
        total={itemPrices.length}
        handleChange={() => { }}
        pagination={false}
      />
      <Modal
        title={t('requirement.addingPrice') + "..."}
        closable={false}
        footer={null}
        maskClosable={false}
        open={matchLoading}
        centered
        width={1000}
      >
        <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center">
          <Spin spinning={matchLoading} size="large" />
        </Flex>
        <Progress percent={Number((processNum / unmatchedItems.length * 100).toFixed(2))} />
        <span>{`${t("requirement.processed")}${processNum}/${unmatchedItems.length}`}</span>
      </Modal>
    </>
  );
});

MatchPrice.displayName = 'MatchPrice';

export default MatchPrice;
